/**
 * 그룹 목록 테이블 컴포넌트
 * - 검색 및 필터링 (활성화 상태)
 * - 페이지네이션
 * - 권한별 액션 버튼 (상세, 수정, 삭제)
 */

import React, { useState, useEffect } from 'react';
import {
  Table,
  Input,
  Select,
  Button,
  Space,
  Tag,
  Dropdown,
  message,
} from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { MenuProps } from 'antd';
import {
  PlusOutlined,
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { groupService } from '../../../services/groupService';
import type { GroupWithStats, GroupListParams } from '../../../types/group';
import { useAuthStore } from '../../../store/authStore';

const { Search } = Input;
const { Option } = Select;

/**
 * 테이블 필터 상태
 */
interface FilterState {
  search: string;
  is_active?: boolean;
}

interface GroupTableProps {
  onCreateGroup: () => void;
  onEdit: (group: GroupWithStats) => void;
  onDelete: (group: GroupWithStats) => void;
}

export const GroupTable: React.FC<GroupTableProps> = ({
  onCreateGroup,
  onEdit,
  onDelete,
}) => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState<GroupWithStats[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState<FilterState>({
    search: '',
  });

  /**
   * 그룹 목록 조회
   */
  const fetchGroups = async () => {
    setLoading(true);
    try {
      const params: GroupListParams = {
        page,
        page_size: pageSize,
        search: filters.search || undefined,
        is_active: filters.is_active,
      };

      const response = await groupService.getGroups(params);
      setGroups(response.groups);
      setTotal(response.total);
    } catch (error) {
      message.error('그룹 목록 조회 실패');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 초기 로드 및 필터/페이지 변경 시 재조회
   */
  useEffect(() => {
    fetchGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, filters]);

  /**
   * 검색 처리
   */
  const handleSearch = (value: string) => {
    setFilters((prev) => ({ ...prev, search: value }));
    setPage(1); // 검색 시 첫 페이지로 이동
  };

  /**
   * 활성 상태 필터 변경
   */
  const handleActiveFilter = (value: boolean | undefined) => {
    setFilters((prev) => ({ ...prev, is_active: value }));
    setPage(1);
  };

  /**
   * 페이지 변경 처리
   */
  const handleTableChange = (pagination: TablePaginationConfig) => {
    setPage(pagination.current || 1);
    setPageSize(pagination.pageSize || 20);
  };

  /**
   * 상세 페이지로 이동
   */
  const handleViewDetail = (groupId: string) => {
    navigate(`/groups/${groupId}`);
  };

  /**
   * 액션 메뉴 아이템 생성
   */
  const getActionMenuItems = (record: GroupWithStats): MenuProps['items'] => {
    const items: MenuProps['items'] = [
      {
        key: 'view',
        icon: <EyeOutlined />,
        label: '상세',
        onClick: () => handleViewDetail(record.id),
      },
      {
        key: 'edit',
        icon: <EditOutlined />,
        label: '수정',
        onClick: () => onEdit(record),
      },
    ];

    // admin만 삭제 가능
    if (currentUser?.role === 'admin') {
      items.push({
        type: 'divider',
      });
      items.push({
        key: 'delete',
        icon: <DeleteOutlined />,
        label: '삭제',
        danger: true,
        onClick: () => onDelete(record),
      });
    }

    return items;
  };

  /**
   * 테이블 컬럼 정의
   */
  const columns: ColumnsType<GroupWithStats> = [
    {
      title: '그룹명',
      dataIndex: 'name',
      key: 'name',
      width: 250,
    },
    {
      title: '사용자 수',
      dataIndex: 'user_count',
      key: 'user_count',
      width: 120,
      render: (count) => `${count}명`,
      sorter: (a, b) => a.user_count - b.user_count,
    },
    {
      title: '강사',
      dataIndex: 'instructor_count',
      key: 'instructor_count',
      width: 100,
      render: (count) => `${count}명`,
    },
    {
      title: '학생',
      dataIndex: 'student_count',
      key: 'student_count',
      width: 100,
      render: (count) => `${count}명`,
    },
    {
      title: '상태',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? '활성' : '비활성'}
        </Tag>
      ),
    },
    {
      title: '생성일',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date) => new Date(date).toLocaleDateString('ko-KR'),
      sorter: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    },
    {
      title: '액션',
      key: 'action',
      width: 80,
      fixed: 'right',
      render: (_, record) => (
        <Dropdown menu={{ items: getActionMenuItems(record) }} trigger={['click']}>
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  return (
    <div>
      {/* 검색 및 필터 */}
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Space>
          <Search
            placeholder="그룹명 또는 코드 검색"
            allowClear
            onSearch={handleSearch}
            style={{ width: 250 }}
          />
          <Select
            placeholder="활성화 상태"
            allowClear
            style={{ width: 150 }}
            onChange={handleActiveFilter}
          >
            <Option value={true}>활성</Option>
            <Option value={false}>비활성</Option>
          </Select>
        </Space>

        {/* admin만 그룹 생성 버튼 표시 */}
        {currentUser?.role === 'admin' && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={onCreateGroup}
          >
            새 그룹
          </Button>
        )}
      </Space>

      {/* 그룹 테이블 */}
      <Table<GroupWithStats>
        columns={columns}
        dataSource={groups}
        rowKey="id"
        loading={loading}
        pagination={{
          current: page,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          showTotal: (total) => `전체 ${total}개`,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
        onChange={handleTableChange}
        scroll={{ x: 1000 }}
      />
    </div>
  );
};
